import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  PlusIcon,
  TrashIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  Bars3Icon,
  ArrowUturnLeftIcon,
} from '@heroicons/react/24/outline';
import { api } from '../../services/api';
import { useAuth } from '../../providers/AuthProvider';
import {
  ADMIN_MENU_BY_ID,
  reconcileMenuConfig,
  defaultMenuConfig,
  type AdminMenuConfig,
  type MenuNode,
  type MenuVisibility,
} from '../../config/adminMenuCatalog';

const VISIBILITY_OPTIONS: { value: MenuVisibility; label: string }[] = [
  { value: 'admin', label: 'All admins' },
  { value: 'super', label: 'Super-admins only' },
  { value: 'hidden', label: 'Hidden' },
];

// ---- pure helpers operating on the nodes array ----

function cloneNodes(nodes: MenuNode[]): MenuNode[] {
  return JSON.parse(JSON.stringify(nodes));
}

interface DraggedItem {
  id: string;
  visibility: MenuVisibility;
}

/** Remove an item by id from anywhere in the tree; return the removed item. */
function pluckItem(nodes: MenuNode[], id: string): DraggedItem | null {
  let found: DraggedItem | null = null;
  for (let i = 0; i < nodes.length; i++) {
    const node = nodes[i];
    if (node.type === 'item' && node.id === id) {
      found = { id: node.id, visibility: node.visibility };
      nodes.splice(i, 1);
      return found;
    }
    if (node.type === 'group') {
      const idx = node.items.findIndex((it) => it.id === id);
      if (idx >= 0) {
        found = { ...node.items[idx] };
        node.items.splice(idx, 1);
        return found;
      }
    }
  }
  return found;
}

export function AdminMenuBuilderPage() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const isSuper = user?.role === 'super_admin';

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'menu-config'],
    queryFn: () => api.getAdminMenuConfig(),
    enabled: isSuper,
  });

  const [nodes, setNodes] = useState<MenuNode[]>([]);
  const [dragId, setDragId] = useState<string | null>(null);
  const [newGroupTitle, setNewGroupTitle] = useState('');
  const [dirty, setDirty] = useState(false);

  // Seed the editor from the saved config (reconciled against the catalog).
  useEffect(() => {
    if (data !== undefined) setNodes(reconcileMenuConfig(data).nodes);
  }, [data]);

  const save = useMutation({
    mutationFn: () => api.saveAdminMenuConfig({ version: 1, nodes } as AdminMenuConfig),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'menu-config'] });
      setDirty(false);
    },
  });

  if (!isSuper) {
    return (
      <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-12 text-center">
        <p className="text-slate-400">Only super-admins can configure the admin menu.</p>
      </div>
    );
  }

  const update = (fn: (draft: MenuNode[]) => void) => {
    setNodes((prev) => {
      const draft = cloneNodes(prev);
      fn(draft);
      return draft;
    });
    setDirty(true);
  };

  // ---- drag-and-drop: move the dragged item before `beforeId`, or append to a group/top ----
  const onDropBeforeItem = (beforeId: string) => {
    if (!dragId || dragId === beforeId) return;
    update((draft) => {
      const item = pluckItem(draft, dragId);
      if (!item) return;
      // locate target container + index
      for (const node of draft) {
        if (node.type === 'item' && node.id === beforeId) {
          draft.splice(draft.indexOf(node), 0, { type: 'item', ...item });
          return;
        }
        if (node.type === 'group') {
          const idx = node.items.findIndex((it) => it.id === beforeId);
          if (idx >= 0) {
            node.items.splice(idx, 0, item);
            return;
          }
        }
      }
    });
    setDragId(null);
  };

  const onDropInGroup = (groupId: string) => {
    if (!dragId) return;
    update((draft) => {
      const item = pluckItem(draft, dragId);
      if (!item) return;
      const group = draft.find((n) => n.type === 'group' && n.id === groupId);
      if (group && group.type === 'group') group.items.push(item);
    });
    setDragId(null);
  };

  const onDropTopLevel = () => {
    if (!dragId) return;
    update((draft) => {
      const item = pluckItem(draft, dragId);
      if (item) draft.push({ type: 'item', ...item });
    });
    setDragId(null);
  };

  const addGroup = () => {
    const title = newGroupTitle.trim();
    if (!title) return;
    update((draft) => {
      draft.push({ type: 'group', id: `grp_${title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}_${draft.length}`, title, items: [] });
    });
    setNewGroupTitle('');
  };

  const renameGroup = (groupId: string, title: string) =>
    update((draft) => {
      const g = draft.find((n) => n.type === 'group' && n.id === groupId);
      if (g && g.type === 'group') g.title = title;
    });

  const deleteGroup = (groupId: string) =>
    update((draft) => {
      const idx = draft.findIndex((n) => n.type === 'group' && n.id === groupId);
      if (idx < 0) return;
      const g = draft[idx] as Extract<MenuNode, { type: 'group' }>;
      // Move its items back to top-level (loose) so nothing is lost.
      const loose: MenuNode[] = g.items.map((it) => ({ type: 'item', ...it }));
      draft.splice(idx, 1, ...loose);
    });

  const moveNode = (index: number, dir: -1 | 1) =>
    update((draft) => {
      const j = index + dir;
      if (j < 0 || j >= draft.length) return;
      [draft[index], draft[j]] = [draft[j], draft[index]];
    });

  const setItemVisibility = (id: string, visibility: MenuVisibility) =>
    update((draft) => {
      for (const node of draft) {
        if (node.type === 'item' && node.id === id) { node.visibility = visibility; return; }
        if (node.type === 'group') {
          const it = node.items.find((x) => x.id === id);
          if (it) { it.visibility = visibility; return; }
        }
      }
    });

  const resetToDefault = () => { setNodes(defaultMenuConfig().nodes); setDirty(true); };

  // ---- render helpers ----
  const ItemRow = ({ id, visibility }: { id: string; visibility: MenuVisibility }) => {
    const item = ADMIN_MENU_BY_ID[id];
    if (!item) return null;
    return (
      <div
        draggable
        onDragStart={() => setDragId(id)}
        onDragEnd={() => setDragId(null)}
        onDragOver={(e) => e.preventDefault()}
        onDrop={() => onDropBeforeItem(id)}
        className={`flex items-center gap-2 px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg cursor-move ${dragId === id ? 'opacity-40' : ''}`}
      >
        <Bars3Icon className="w-4 h-4 text-slate-500 shrink-0" />
        <item.icon className="w-4 h-4 text-slate-400 shrink-0" />
        <span className="text-sm text-white flex-1 truncate">{item.label}</span>
        <select
          value={visibility}
          onChange={(e) => setItemVisibility(id, e.target.value as MenuVisibility)}
          className="px-2 py-1 bg-slate-700 border border-slate-600 rounded text-xs text-white focus:border-cyan-500 focus:outline-none"
        >
          {VISIBILITY_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-white">Menu Builder</h1>
          <p className="text-slate-400 mt-1">Arrange the admin sidebar: drag items into groups, reorder, and set who sees each item.</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={resetToDefault} className="flex items-center gap-2 px-3 py-2 text-sm text-slate-400 hover:text-white transition-colors">
            <ArrowUturnLeftIcon className="w-4 h-4" /> Reset to default
          </button>
          <button
            onClick={() => save.mutate()}
            disabled={!dirty || save.isPending}
            className="px-4 py-2 text-sm bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg disabled:opacity-50 transition-colors"
          >
            {save.isPending ? 'Saving…' : 'Save menu'}
          </button>
        </div>
      </div>

      {save.isError && <p className="text-sm text-red-400">Could not save the menu. Please try again.</p>}
      {save.isSuccess && !dirty && <p className="text-sm text-green-400">Menu saved. It updates for all admins on their next load.</p>}

      {/* Add group */}
      <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4 flex items-center gap-2">
        <input
          value={newGroupTitle}
          onChange={(e) => setNewGroupTitle(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && addGroup()}
          placeholder="New group title (e.g. Operations)"
          className="flex-1 px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm focus:border-cyan-500 focus:outline-none"
        />
        <button onClick={addGroup} disabled={!newGroupTitle.trim()} className="flex items-center gap-2 px-4 py-2 text-sm bg-slate-600 hover:bg-slate-500 text-white rounded-lg disabled:opacity-50 transition-colors">
          <PlusIcon className="w-4 h-4" /> Add group
        </button>
      </div>

      {isLoading ? (
        <div className="text-slate-400 text-sm">Loading…</div>
      ) : (
        <div className="space-y-2">
          {nodes.map((node, index) => {
            if (node.type === 'item') {
              return (
                <div key={`item-${node.id}`} className="flex items-center gap-2">
                  <div className="flex flex-col">
                    <button onClick={() => moveNode(index, -1)} disabled={index === 0} className="text-slate-500 hover:text-white disabled:opacity-30"><ChevronUpIcon className="w-4 h-4" /></button>
                    <button onClick={() => moveNode(index, 1)} disabled={index === nodes.length - 1} className="text-slate-500 hover:text-white disabled:opacity-30"><ChevronDownIcon className="w-4 h-4" /></button>
                  </div>
                  <div className="flex-1"><ItemRow id={node.id} visibility={node.visibility} /></div>
                </div>
              );
            }
            // group
            return (
              <div key={`group-${node.id}`} className="flex items-start gap-2">
                <div className="flex flex-col pt-3">
                  <button onClick={() => moveNode(index, -1)} disabled={index === 0} className="text-slate-500 hover:text-white disabled:opacity-30"><ChevronUpIcon className="w-4 h-4" /></button>
                  <button onClick={() => moveNode(index, 1)} disabled={index === nodes.length - 1} className="text-slate-500 hover:text-white disabled:opacity-30"><ChevronDownIcon className="w-4 h-4" /></button>
                </div>
                <div
                  className="flex-1 bg-slate-800/40 border border-slate-700 rounded-xl p-3"
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={() => onDropInGroup(node.id)}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <input
                      value={node.title}
                      onChange={(e) => renameGroup(node.id, e.target.value)}
                      className="flex-1 px-2 py-1 bg-transparent border-b border-slate-700 text-sm font-semibold text-white uppercase tracking-wider focus:border-cyan-500 focus:outline-none"
                    />
                    <button onClick={() => deleteGroup(node.id)} className="p-1.5 text-slate-400 hover:text-red-400 hover:bg-slate-700 rounded transition-colors" title="Delete group (items move to top level)">
                      <TrashIcon className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="space-y-1.5 min-h-[2.5rem]">
                    {node.items.length === 0 ? (
                      <p className="text-xs text-slate-500 italic px-1 py-2">Drag items here</p>
                    ) : (
                      node.items.map((it) => <ItemRow key={it.id} id={it.id} visibility={it.visibility} />)
                    )}
                  </div>
                </div>
              </div>
            );
          })}

          {/* Drop zone to move an item back to top level */}
          <div
            onDragOver={(e) => e.preventDefault()}
            onDrop={onDropTopLevel}
            className="border-2 border-dashed border-slate-700 rounded-xl p-4 text-center text-xs text-slate-500"
          >
            Drop here to move an item to the top level (no group)
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminMenuBuilderPage;
